/*
  Klaviyo event tracking — centralized module.

  Replaces the legacy inline _learnq snippets that lived in Shopify Admin
  "Additional Scripts" (Viewed Product, Added to Cart, Back-in-stock init).
  Everything here is data-driven by attributes / settings; no hardcoded
  selectors or product-specific shapes.

  Architecture:
  --------------
  Liquid emits structured payloads at page render time via the
  snippets/klaviyo-tracking.liquid snippet — each pushes a queued event onto
  window.__klaviyoQueue. This module drains the queue once on load. Driving
  via a queue (rather than firing inline) gives us:
    1. A single chokepoint for instrumentation, error handling, deduping.
    2. No race with the Klaviyo onsite SDK boot — the queue is drained as
       soon as document is interactive; if the SDK isn't ready, _learnq
       buffers it (the SDK replays the queue once it boots).
    3. Trivial to mock in tests or feature-flag by surface.

  Runtime events (not Liquid-emitted) hook into the theme's pubsub:
    - PUB_SUB_EVENTS.cartUpdate    -> "Added to Cart"
    - PUB_SUB_EVENTS.variantChange -> updates the in-memory variant context
                                      so ATC payloads reflect the chosen
                                      variant, not the initial one.

  Failure mode philosophy:
  ------------------------
  Every track() call is wrapped in try/catch and only console.warn's on
  failure — Klaviyo analytics is never load-bearing for the shopper. The
  module is safe to ship before company-id/list-id are configured (it
  becomes a no-op).
*/

(function () {
  'use strict';

  /*
    Klaviyo's modern surface exposes `window.klaviyo.push(['track', name, props])`
    (and a direct `track()` method on newer versions). The legacy `_learnq`
    queue still works and the SDK auto-mirrors push() calls into it on the
    way down. We try `klaviyo.push` first, fall back to `_learnq.push`,
    fall back to lazy-init queue if Klaviyo never loads.
  */
  function pushKlaviyo(args) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(args);
        return;
      }
      window._learnq = window._learnq || [];
      window._learnq.push(args);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[klaviyo-events] push failed', args, e);
      }
    }
  }

  function track(eventName, properties) {
    if (!eventName) return;
    pushKlaviyo(['track', eventName, properties || {}]);
  }

  function identify(properties) {
    if (!properties || !properties.email) return;
    pushKlaviyo(['identify', properties]);
  }

  /*
    Drain Liquid-emitted events. Snippet outputs:
      window.__klaviyoQueue.push({ type: 'track', name: '...', properties: {...} });
      window.__klaviyoQueue.push({ type: 'identify', properties: {...} });
    We process the queue and replace .push with a synchronous track() so
    any straggler enqueued later (e.g. by a slow-loading section) fires
    immediately instead of pooling forever.
  */
  function drainQueue() {
    var q = window.__klaviyoQueue;
    if (!q || !Array.isArray(q)) {
      window.__klaviyoQueue = { push: handleQueueEntry };
      return;
    }
    while (q.length) {
      handleQueueEntry(q.shift());
    }
    window.__klaviyoQueue = { push: handleQueueEntry };
  }

  function handleQueueEntry(entry) {
    if (!entry || !entry.type) return;
    if (entry.type === 'track') {
      track(entry.name, entry.properties);
    } else if (entry.type === 'identify') {
      identify(entry.properties);
    }
  }

  /*
    In-memory variant context. Liquid seeds it on the PDP via the same
    queue (entry { type: 'context', key: 'variant', value: {...} }). The
    variantChange pubsub event updates it as the shopper swaps options,
    so the next Added to Cart fires with the correct variant.
  */
  var variantContext = null;

  function handleContextEntry(entry) {
    if (entry && entry.type === 'context' && entry.key === 'variant') {
      variantContext = entry.value || null;
    }
  }

  // Extend handleQueueEntry to also accept 'context' entries.
  var _origHandle = handleQueueEntry;
  handleQueueEntry = function (entry) {
    if (entry && entry.type === 'context') {
      handleContextEntry(entry);
      return;
    }
    _origHandle(entry);
  };

  function buildAddedToCartProperties(payload) {
    /*
      The Wonder theme publishes cartUpdate with the raw /cart/add JSON
      response in cartData. That payload has the full line item shape
      under cartData.items_added (newer Shopify) or as a flat object on
      cartData itself when only one line was added (older). Be defensive
      across both.
    */
    if (!payload || !payload.cartData) return null;
    var data = payload.cartData;
    var line = null;
    if (Array.isArray(data.items) && data.items.length) {
      line = data.items[0];
    } else if (data.id) {
      // Older Shopify returns a single line item at top level.
      line = data;
    }
    if (!line) return null;

    var priceCents = typeof line.final_price === 'number' ? line.final_price : line.price;
    var compareCents =
      typeof line.original_price === 'number' ? line.original_price : line.compare_at_price;

    var props = {
      ProductID: line.product_id,
      ProductName: line.product_title || line.title,
      ProductHandle: line.handle || line.url ? (line.url || '').split('/products/').pop().split('?')[0] : null,
      VariantID: line.variant_id || line.id,
      VariantTitle: line.variant_title || (variantContext && variantContext.title) || null,
      VariantSKU: line.sku || null,
      Quantity: line.quantity || 1,
      Price: typeof priceCents === 'number' ? priceCents / 100 : null,
      CompareAtPrice: typeof compareCents === 'number' && compareCents > priceCents ? compareCents / 100 : null,
      ImageURL: line.featured_image
        ? (typeof line.featured_image === 'string' ? line.featured_image : line.featured_image.url)
        : variantContext && variantContext.image
        ? variantContext.image
        : null,
      URL: line.url ? buildAbsoluteUrl(line.url) : null,
      Source: payload.source || 'unknown',
    };

    // Strip nulls so Klaviyo's event log stays clean.
    Object.keys(props).forEach(function (k) {
      if (props[k] === null || props[k] === undefined) delete props[k];
    });
    return props;
  }

  function buildAbsoluteUrl(path) {
    if (!path) return null;
    if (path.indexOf('http') === 0) return path;
    try {
      return new URL(path, window.location.origin).toString();
    } catch (_) {
      return path;
    }
  }

  function init() {
    drainQueue();

    /*
      Hook the theme's cart pubsub. Every add-to-cart path (PDP, accessory
      upsell, in-cart cross-sell, quick-add modal) publishes cartUpdate
      through assets/product-form.js, assets/custom.js, etc. — so one
      subscription here replaces the brittle click-listener-on-a-selector
      that the legacy snippet used.

      Toggle: window.__klaviyoConfig.trackAddedToCart is emitted by
      snippets/klaviyo-tracking.liquid from the per-event theme
      setting. Defaults OFF in the schema because Shopify's native
      Klaviyo app integration syncs cart events server-side already
      — we don't want to double-fire. Merchants can flip it on if
      they've turned off the Shopify-side sync, or if they want the
      richer payload (Source, ImageURL, etc.) this module emits.
    */
    var cfg = window.__klaviyoConfig || {};
    try {
      if (
        cfg.trackAddedToCart &&
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS === 'object' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
          var props = buildAddedToCartProperties(payload);
          if (props) track('Added to Cart', props);
        });
      }

      /*
        Keep the variant context fresh. variantChange fires from
        assets/variants.js whenever the shopper picks a different option
        combination. We only need the bits ATC consumes; storing the
        whole variant blob is wasteful.
      */
      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS === 'object' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
          var v = payload && payload.variant;
          if (!v) return;
          variantContext = {
            id: v.id,
            title: v.title,
            sku: v.sku,
            price: typeof v.price === 'number' ? v.price / 100 : v.price,
            image: v.featured_image
              ? (typeof v.featured_image === 'string'
                ? v.featured_image
                : (v.featured_image.src ? 'https:' + v.featured_image.src : null))
              : null,
          };
        });
      }
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[klaviyo-events] pubsub bind failed', e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /*
    Tiny public surface in case other modules want to fire ad-hoc events
    without re-implementing the SDK probing logic. Intentionally not
    documented for merchants — internal to the theme.
  */
  window.__klaviyoEvents = {
    track: track,
    identify: identify,
  };
})();
